import { useState, useEffect } from "react";
import axios from "axios";
import baseURL from "../config";
import {
  Description as InvoiceIcon,
  CheckCircle as SuccessIcon,
  People as UsersIcon,
  Autorenew as LoaderIcon,
} from "@mui/icons-material";
import {
  Card,
  CardContent,
  TextField,
  MenuItem,
  Button,
  Divider,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";

export default function MonthlyInvoiceGenerator() {
  const [crewingAgents, setCrewingAgents] = useState([]);
  const [crewingAgentId, setCrewingAgentId] = useState("");
  const [month, setMonth] = useState("");
  const [loading, setLoading] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(null);

  // ✅ Fetch all crewing agents on page load
  useEffect(() => {
    const token = localStorage.getItem("token");
    const fetchAgents = async () => {
      try {
        const res = await axios.get(
          `${baseURL}/api/crewingAgent/getApprovedCrewingAgent`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const agents = Array.isArray(res.data) ? res.data : res.data.data;
        setCrewingAgents(agents || []);
      } catch (err) {
        console.error("Error fetching agents:", err);
        setError("Failed to load agents");
      }
    };
    fetchAgents();
  }, []);

  const handleGenerate = async () => {
    if (!crewingAgentId || !month) {
      setError("⚠️ Please select an Agent and Month");
      return;
    }
    setError(null);
    setLoading(true);

    try {
      const res = await axios.post(
        `${baseURL}/api/crewingAgentinvoices/generate`,
        { crewingAgentId, month }
      );
      setInvoice(res.data.invoice);
    } catch (err) {
      setError(err.response?.data?.error || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto mt-12 space-y-10">
      {/* Heading */}
      <div className="flex items-center gap-3">
        <InvoiceIcon color="primary" fontSize="large" />
        <h2 className="text-3xl font-bold text-gray-800">
          Monthly Invoice Generator
        </h2>
      </div>

      {/* Form Card */}
      <Card className="shadow-md rounded-2xl">
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Crewing Agent Dropdown */}
            <TextField
              select
              label="Select Crewing Agent"
              value={crewingAgentId}
              onChange={(e) => setCrewingAgentId(e.target.value)}
              fullWidth
              variant="outlined"
            >
              <MenuItem value="">-- Select Agent --</MenuItem>
              {crewingAgents.map((agent) => (
                <MenuItem key={agent._id} value={agent._id}>
                  {agent.agentName} ({agent.contactNumber})
                </MenuItem>
              ))}
            </TextField>

            {/* Month Input */}
            <TextField
              type="month"
              label="Month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
          </div>

          {/* Generate Button */}
          <Button
            onClick={handleGenerate}
            disabled={loading}
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            startIcon={
              loading ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            {loading ? "Generating..." : "Generate Invoice"}
          </Button>

          {/* Error Message */}
          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Invoice Result */}
      {invoice && (
        <Card className="shadow-lg rounded-2xl">
          <CardContent>
            <div className="flex items-center gap-2 mb-4">
              <SuccessIcon className="text-green-600" />
              <Typography variant="h6" className="font-semibold">
                Invoice Generated
              </Typography>
            </div>

            <Typography variant="body2" color="textSecondary" gutterBottom>
              Invoice ID: {invoice._id}
            </Typography>

            {/* Summary Grid */}
            <div className="grid grid-cols-2 gap-4 my-4 text-gray-700">
              <p>
                <span className="font-semibold">Agent:</span>{" "}
                {invoice.crewingAgentName}
              </p>
              <p>
                <span className="font-semibold">Mobile:</span>{" "}
                {invoice.crewingAgentMobile}
              </p>
              <p>
                <span className="font-semibold">Month:</span> {invoice.month}
              </p>
              <p className="text-lg font-bold text-blue-700">
                Total: ₹{invoice.totalAgentAmount}
              </p>
            </div>

            <Divider className="my-4" />

            {/* Crew Charges Table */}
            <div className="mt-6">
              <Typography
                variant="subtitle1"
                className="font-semibold flex items-center gap-2 mb-2"
              >
                <UsersIcon color="primary" /> Crew Charges
              </Typography>

              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Crew Name</TableCell>
                    <TableCell>Indos No</TableCell>
                    <TableCell align="right">Amount (₹)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice.crews.map((c, index) => (
                    <TableRow
                      key={c.crewId}
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent",
                      }}
                    >
                      <TableCell>{c.crewName}</TableCell>
                      <TableCell>{c.crewIndosNo}</TableCell>
                      <TableCell align="right">
                        ₹{c.totalCrewAmount}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
